import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { Info } from "@material-ui/icons";
import Checkbox from "@saleor/components/Checkbox";
import Money from "@saleor/components/Money";
import Percent from "@saleor/components/Percent";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { BulkVoucherList_voucherGroup_vouchers_edges_node } from "@saleor/discounts/types/BulkVoucherList";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, PageListProps } from "@saleor/types";
import {
  DiscountValueTypeEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "@saleor/types/globalTypes";
import { getFooterColSpanWithBulkActions } from "@saleor/utils/tables";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

export interface BulkVoucherListProps
  extends ListProps,
    ListActions,
    PageListProps {
  defaultCurrency: string;
  vouchers: BulkVoucherList_voucherGroup_vouchers_edges_node[];
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colEnd: {
        width: 180
      },
      colMinSpent: {
        width: 180
      },
      colName: {},
      colStart: {
        width: 180
      },
      colUses: {
        width: 250
      },
      colValue: {
        width: 100
      }
    },
    colEnd: {
      textAlign: "right"
    },
    colMinSpent: {
      textAlign: "center"
    },
    colName: {
      overflow: "hidden",
      paddingLeft: 0,
      textOverflow: "ellipsis",
      width: 300
    },
    colStart: {
      textAlign: "right"
    },
    colType: {
      textAlign: "center",
      width: 200
    },
    colUses: {
      textAlign: "center"
    },
    colValue: {
      textAlign: "right"
    },
    tableRow: {
      cursor: "pointer"
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "VoucherList" }
);

const numberOfColumns = 7;
const tableHeadNumCol = 8;

const BulkVoucherList: React.FC<BulkVoucherListProps> = props => {
  const {
    settings,
    disabled,
    defaultCurrency,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    pageInfo,
    onRowClick,
    vouchers,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;

  const classes = useStyles(props);
  function getVoucherType(data) {
    let value = "";
    const type = data?.maxDiscountValue
      ? VoucherDiscountType.PERCENTAGE_AND_CAPPED
      : data?.type === VoucherTypeEnum.SHIPPING
      ? VoucherTypeEnum.SHIPPING
      : data?.type === VoucherTypeEnum.E_VOUCHER
      ? VoucherTypeEnum.E_VOUCHER
      : data.discountValueType;

    switch (type) {
      case VoucherDiscountType.PERCENTAGE_AND_CAPPED:
        value = "CAPPED AND PERCENTAGE";
        break;
      case VoucherTypeEnum.SHIPPING:
        value = "FREE SHIPPING";
        break;
      case VoucherTypeEnum.E_VOUCHER:
        value = "E-VOUCHER";
        break;
      case "FIXED":
        value = "FIXED AMOUNT";
        break;
      default:
        value = "PERCENTAGE";
        break;
    }
    return value;
  }

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={tableHeadNumCol}
        selected={selected}
        disabled={disabled}
        items={vouchers}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader textAlign="left" className={classes.colName}>
          <FormattedMessage
            defaultMessage="Voucher Code"
            description="voucher code"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="center" className={classes.colType}>
          <FormattedMessage
            defaultMessage="Discount Type"
            description="voucher type"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="right" className={classes.colMinSpent}>
          <FormattedMessage
            defaultMessage="Minimum Spent"
            description="minimum amount of spent money to activate voucher"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="right" className={classes.colstart}>
          <FormattedMessage
            defaultMessage="Start Date"
            description="voucher is active from date"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="right" className={classes.colend}>
          <FormattedMessage
            defaultMessage="End date"
            description="voucher is active until date"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="right" className={classes.colvalue}>
          <FormattedMessage
            defaultMessage="Discount Value"
            description="voucher value"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="right" className={classes.colMinSpent}>
          <FormattedMessage
            defaultMessage="Voucher Status"
            description="voucher status"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={getFooterColSpanWithBulkActions(vouchers, numberOfColumns)}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          vouchers,
          (voucher, index) => {
            const isSelected = voucher ? isChecked(voucher.id) : false;

            return (
              <TableRow
                className={!!voucher ? classes.tableRow : undefined}
                hover={!!voucher}
                key={voucher ? voucher.id : "skeleton"}
                selected={isSelected}
                onClick={voucher ? onRowClick(voucher.id) : undefined}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    id={`voucherCheckboxRow${index}`}
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(voucher.id)}
                  />
                </TableCell>
                <TableCell
                  className={classes.colName}
                  id={`voucherCodeRow${index}`}
                >
                  {maybe<React.ReactNode>(() => voucher.code, <Skeleton />)}
                  {maybe<React.ReactNode>(() =>
                    voucher.isHidden ? (
                      <Tooltip title="Hidden" aria-label="add">
                        <Info fontSize="small" />
                      </Tooltip>
                    ) : (
                      ""
                    )
                  )}
                </TableCell>
                <TableCell
                  className={classes.colType}
                  id={`voucherDiscountTypeRow${index}`}
                >
                  {maybe<React.ReactNode>(
                    () => getVoucherType(voucher),
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colMinSpent}
                  id={`voucherMinimumSpentRow${index}`}
                >
                  {voucher && voucher.minSpent ? (
                    <Money money={voucher.minSpent} />
                  ) : voucher && voucher.minSpent === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colStart}
                  id={`voucherStartDateRow${index}`}
                >
                  {voucher && voucher.startDate ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {voucher?.startDate}
                    </Moment>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colEnd}
                  id={`voucherEndDateRow${index}`}
                >
                  {voucher && voucher.endDate ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {voucher?.endDate}
                    </Moment>
                  ) : voucher && voucher.endDate === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colValue}
                  id={`voucherValueRow${index}`}
                >
                  {voucher &&
                  voucher.discountValueType &&
                  voucher.discountValue ? (
                    voucher.discountValueType ===
                    DiscountValueTypeEnum.FIXED ? (
                      <Money
                        money={{
                          amount: voucher.discountValue,
                          currency: defaultCurrency
                        }}
                      />
                    ) : (
                      <Percent amount={voucher.discountValue} />
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colUses}
                  id={`voucherStatusRow${index}`}
                >
                  {maybe<React.ReactNode>(() => voucher.status, <Skeleton />)}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No vouchers found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
BulkVoucherList.displayName = "BulkVoucherList";
export default BulkVoucherList;
